import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import '../AuthShared.css';

import API, { setAuthToken } from '../../Utils/api';
import { useUser } from '../../Context/useUser';
import { useNavigate } from 'react-router';

import { toast } from 'react-toastify';

export default function LoginPage() {
    const navigate = useNavigate();
    const { setUser } = useUser();

    const [showingLogin, setShowingLogin] = useState(true);

    const {
        register: registerLogin,
        handleSubmit: handleSubmitLogin,
        formState: { errors: loginErrors }
    } = useForm({ mode: 'onBlur' });

    const {
        register: registerRegister,
        handleSubmit: handleSubmitRegister,
        watch,
        formState: { errors: registerErrors }
    } = useForm({ mode: 'onBlur' });

    const password = watch('password');

    const login = async ({ email, password }) => {
        try {
            const { data } = await API.login({ email, password });

            // The token has to be on the axios default header before any
            // protected request. useUser only sets it during its 5-minute
            // refresh, which would leave the first calls unauthenticated.
            setAuthToken(data.accessToken);
            setUser(data.user);
            navigate('/');
        } catch {
            toast.error('Credentials were not correct!', {
                position: 'bottom-center'
            });
        }
    };

    const register = async ({ email, password, firstName, lastName, type }) => {
        try {
            await API.signup({ email, password, firstName, lastName, type });
            toast.success('Account created. You can log in now.', {
                position: 'bottom-center'
            });
            setShowingLogin(true);
        } catch {
            toast.error('Could not create the account.', {
                position: 'bottom-center'
            });
        }
    };

    return (
        <div className='auth'>
            <div className='auth-card'>
                <div className='auth-head'>
                    <h1>{showingLogin ? 'Welcome back' : 'Create your account'}</h1>
                    <p>
                        {showingLogin
                            ? 'Log in to see what today asks of you.'
                            : 'Pick your role. You can change it later from your profile.'}
                    </p>
                </div>

                <div className='auth-tabs' role='tablist' aria-label='Log in or sign up'>
                    <button
                        type='button'
                        role='tab'
                        aria-selected={showingLogin}
                        className={showingLogin ? 'auth-tab is-active' : 'auth-tab'}
                        onClick={() => setShowingLogin(true)}
                    >
                        Log in
                    </button>
                    <button
                        type='button'
                        role='tab'
                        aria-selected={!showingLogin}
                        className={!showingLogin ? 'auth-tab is-active' : 'auth-tab'}
                        onClick={() => setShowingLogin(false)}
                    >
                        Sign up
                    </button>
                </div>

                {showingLogin ? (
                    <form onSubmit={handleSubmitLogin(login)} noValidate>
                        <div className='auth-field'>
                            <label htmlFor='login-email'>Email address</label>
                            <input
                                id='login-email'
                                type='email'
                                autoComplete='email'
                                aria-invalid={loginErrors.email ? 'true' : undefined}
                                {...registerLogin('email', {
                                    required: 'Enter your email address.',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'That email address does not look right.'
                                    }
                                })}
                            />
                            {loginErrors.email && (
                                <p className='auth-error' role='alert'>
                                    {loginErrors.email.message}
                                </p>
                            )}
                        </div>

                        <div className='auth-field'>
                            <label htmlFor='login-password'>Password</label>
                            <input
                                id='login-password'
                                type='password'
                                autoComplete='current-password'
                                aria-invalid={loginErrors.password ? 'true' : undefined}
                                {...registerLogin('password', {
                                    required: 'Enter your password.'
                                })}
                            />
                            {loginErrors.password && (
                                <p className='auth-error' role='alert'>
                                    {loginErrors.password.message}
                                </p>
                            )}
                        </div>

                        <div className='auth-row'>
                            <button
                                type='button'
                                className='auth-link'
                                onClick={() => navigate('/forgot-password')}
                            >
                                Forgot password?
                            </button>
                        </div>

                        <button type='submit' className='auth-submit'>
                            Log in
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleSubmitRegister(register)} noValidate>
                        <div className='auth-field'>
                            <label htmlFor='signup-email'>Email address</label>
                            <input
                                id='signup-email'
                                type='email'
                                autoComplete='email'
                                aria-invalid={registerErrors.email ? 'true' : undefined}
                                {...registerRegister('email', {
                                    required: 'Enter your email address.',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'That email address does not look right.'
                                    }
                                })}
                            />
                            {registerErrors.email && (
                                <p className='auth-error' role='alert'>
                                    {registerErrors.email.message}
                                </p>
                            )}
                        </div>

                        <div className='auth-grid'>
                            <div className='auth-field'>
                                <label htmlFor='signup-first'>First name</label>
                                <input
                                    id='signup-first'
                                    type='text'
                                    autoComplete='given-name'
                                    aria-invalid={registerErrors.firstName ? 'true' : undefined}
                                    {...registerRegister('firstName', {
                                        required: 'Enter your first name.'
                                    })}
                                />
                                {registerErrors.firstName && (
                                    <p className='auth-error' role='alert'>
                                        {registerErrors.firstName.message}
                                    </p>
                                )}
                            </div>

                            <div className='auth-field'>
                                <label htmlFor='signup-last'>Last name</label>
                                <input
                                    id='signup-last'
                                    type='text'
                                    autoComplete='family-name'
                                    aria-invalid={registerErrors.lastName ? 'true' : undefined}
                                    {...registerRegister('lastName', {
                                        required: 'Enter your last name.'
                                    })}
                                />
                                {registerErrors.lastName && (
                                    <p className='auth-error' role='alert'>
                                        {registerErrors.lastName.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='auth-field'>
                            <label htmlFor='signup-role'>I am a</label>
                            <select
                                id='signup-role'
                                aria-invalid={registerErrors.type ? 'true' : undefined}
                                {...registerRegister('type', { required: 'Pick a role.' })}
                            >
                                <option value=''>Select a role</option>
                                <option value='Athlete'>Athlete</option>
                                <option value='Coach'>Coach</option>
                            </select>
                            {registerErrors.type && (
                                <p className='auth-error' role='alert'>
                                    {registerErrors.type.message}
                                </p>
                            )}
                        </div>

                        <div className='auth-field'>
                            <label htmlFor='signup-password'>Password</label>
                            <input
                                id='signup-password'
                                type='password'
                                autoComplete='new-password'
                                aria-invalid={registerErrors.password ? 'true' : undefined}
                                {...registerRegister('password', {
                                    required: 'Choose a password.',
                                    minLength: {
                                        value: 8,
                                        message: 'Use at least 8 characters.'
                                    }
                                })}
                            />
                            {registerErrors.password && (
                                <p className='auth-error' role='alert'>
                                    {registerErrors.password.message}
                                </p>
                            )}
                        </div>

                        {/* confirmPassword was collected before but never compared
                            against password and never sent, so a mismatch passed. */}
                        <div className='auth-field'>
                            <label htmlFor='signup-confirm'>Confirm password</label>
                            <input
                                id='signup-confirm'
                                type='password'
                                autoComplete='new-password'
                                aria-invalid={
                                    registerErrors.confirmPassword ? 'true' : undefined
                                }
                                {...registerRegister('confirmPassword', {
                                    required: 'Confirm your password.',
                                    validate: value =>
                                        value === password || 'The passwords do not match.'
                                })}
                            />
                            {registerErrors.confirmPassword && (
                                <p className='auth-error' role='alert'>
                                    {registerErrors.confirmPassword.message}
                                </p>
                            )}
                        </div>

                        <button type='submit' className='auth-submit'>
                            Sign up
                        </button>
                    </form>
                )}

                <p className='auth-swap'>
                    {showingLogin ? 'Not a member? ' : 'Already have an account? '}
                    <button
                        type='button'
                        className='auth-link'
                        onClick={() => setShowingLogin(!showingLogin)}
                    >
                        {showingLogin ? 'Sign up now' : 'Log in'}
                    </button>
                </p>
            </div>
        </div>
    );
}
